import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { companiesApi, customerProfilesApi } from "../services/api";
import { useNotification } from "../context/NotificationContext";

const EMPTY_FORM = { name: "", domain: "", ai_instructions: "" };

export default function CompaniesPage() {
  const qc = useQueryClient();
  const { addNotification } = useNotification();
  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [editing, setEditing]     = useState(null); // { id, name, domain }

  const { data: companies = [], isLoading, isError: listFailed, refetch: refetchList } = useQuery({
    queryKey: ["companies"],
    queryFn: () => companiesApi.list(),
  });

  // Which companies already have an IT-environment profile — Rule 31
  // (environment-aware AI diagnosis) can only help a company's tickets once
  // this is filled in, so surfacing the gap here helps prioritize onboarding.
  // GET /companies/profiles/all is admin-only (unlike /companies/ itself, which any technician can
  // load), so a non-admin viewing this page always gets a 403 here -- without isError, that silently
  // resolved to an empty profiles list, making the "no IT profile" warning wrongly show for EVERY
  // company instead of only the ones that actually lack one.
  const { data: profiles = [], isError: profilesFailed } = useQuery({
    queryKey: ["customer-profiles-all"],
    queryFn: () => customerProfilesApi.list(),
    staleTime: 60000,
  });
  const companyIdsWithProfile = new Set(profiles.map((p) => p.company_id));

  const createMutation = useMutation({
    mutationFn: companiesApi.create,
    onSuccess: () => {
      addNotification("חברה נוצרה בהצלחה", "success");
      qc.invalidateQueries({ queryKey: ["companies"] });
      setShowForm(false);
      setForm(EMPTY_FORM);
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה ביצירת החברה", "error"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => companiesApi.update(id, data),
    onSuccess: () => {
      addNotification("פרטי החברה עודכנו", "success");
      qc.invalidateQueries({ queryKey: ["companies"] });
      setEditing(null);
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בעדכון פרטי החברה", "error"),
  });

  const deleteMutation = useMutation({
    mutationFn: companiesApi.delete,
    onSuccess: () => {
      addNotification("החברה נמחקה", "info");
      qc.invalidateQueries({ queryKey: ["companies"] });
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה במחיקת החברה", "error"),
  });

  const handleCreate = (e) => {
    e.preventDefault();
    createMutation.mutate(form);
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    updateMutation.mutate({ id: editing.id, data: { name: editing.name, domain: editing.domain, ai_instructions: editing.ai_instructions || null } });
  };

  return (
    <div className="space-y-5 max-w-4xl mx-auto" dir="rtl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">ניהול חברות</h1>
        <button
          onClick={() => { setShowForm(!showForm); setEditing(null); }}
          className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          + חברה חדשה
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-white rounded-xl shadow-sm border p-6 space-y-4">
          <h2 className="font-semibold text-gray-700">הוספת חברה</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">שם החברה</label>
              <input
                required
                placeholder="Acme Systems"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">דומיין (ללא @)</label>
              <input
                required
                placeholder="acme.co.il"
                value={form.domain}
                onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value.toLowerCase() }))}
                className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
              />
              <p className="text-xs text-gray-400 mt-1">מיילים מדומיין זה ישובצו אוטומטית לחברה</p>
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">הוראות AI לחברה <span className="text-gray-300">(אופציונלי)</span></label>
            <textarea
              rows={3}
              placeholder="לדוגמה: הלקוח משתמש תמיד ב-Windows 10. אל תציע פתרונות ל-macOS."
              value={form.ai_instructions}
              onChange={(e) => setForm((f) => ({ ...f, ai_instructions: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm resize-y"
            />
            <p className="text-xs text-gray-400 mt-1">הוראות אלו יועברו ל-AI בכל ניתוח קריאה עבור חברה זו</p>
          </div>
          {createMutation.isError && (
            <p className="text-red-600 text-sm">{createMutation.error?.response?.data?.detail}</p>
          )}
          <div className="flex gap-2">
            <button type="submit" disabled={createMutation.isPending}
              className="bg-brand-600 text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors">
              {createMutation.isPending ? "שומר..." : "הוסף חברה"}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }}
              className="border px-5 py-2 rounded-lg text-sm hover:bg-gray-50">
              ביטול
            </button>
          </div>
        </form>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
        </div>
      ) : listFailed && companies.length === 0 ? (
        // a failed request is not "no companies"
        <div role="alert" className="text-center py-16 text-red-600">
          <p className="font-medium">לא ניתן לטעון את רשימת החברות</p>
          <button onClick={() => refetchList()} className="mt-2 text-sm text-brand-600 hover:underline">נסה שוב</button>
        </div>
      ) : companies.length === 0 ? (
        <div className="text-center py-16 text-gray-400">לא קיימות חברות במערכת</div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b text-gray-500 text-xs uppercase">
              <tr>
                <th className="text-right px-4 py-3">שם חברה</th>
                <th className="text-right px-4 py-3">דומיין</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {companies.map((c) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-gray-50">
                  {editing?.id === c.id ? (
                    <td colSpan={3} className="px-4 py-3">
                      <form onSubmit={handleUpdate} className="space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            value={editing.name}
                            onChange={(e) => setEditing((ed) => ({ ...ed, name: e.target.value }))}
                            className="border rounded px-2 py-1 text-sm w-full"
                            placeholder="שם החברה"
                          />
                          <input
                            value={editing.domain}
                            onChange={(e) => setEditing((ed) => ({ ...ed, domain: e.target.value.toLowerCase() }))}
                            className="border rounded px-2 py-1 text-sm w-full font-mono"
                            placeholder="domain.co.il"
                          />
                        </div>
                        <textarea
                          rows={3}
                          value={editing.ai_instructions || ""}
                          onChange={(e) => setEditing((ed) => ({ ...ed, ai_instructions: e.target.value }))}
                          className="border rounded px-2 py-1 text-sm w-full resize-y"
                          placeholder="הוראות AI לחברה (אופציונלי) — יועברו ל-AI בכל ניתוח קריאה"
                        />
                        <div className="flex gap-2">
                          <button
                            type="submit"
                            disabled={updateMutation.isPending}
                            className="text-xs bg-brand-600 text-white px-3 py-1 rounded disabled:opacity-50"
                          >
                            שמור
                          </button>
                          <button type="button" onClick={() => setEditing(null)} className="text-xs border px-3 py-1 rounded hover:bg-gray-50">
                            ביטול
                          </button>
                        </div>
                      </form>
                    </td>
                  ) : (
                    <>
                      <td className="px-4 py-3 font-medium text-gray-800">
                        {c.name}
                        {c.ai_instructions && (
                          <span className="mr-2 text-[10px] bg-indigo-50 text-indigo-500 border border-indigo-200 rounded px-1" title={c.ai_instructions}>AI</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-gray-600 text-xs">{c.domain}</td>
                      <td className="px-4 py-3 text-left">
                        <div className="flex gap-2 justify-end items-center">
                          {!profilesFailed && !companyIdsWithProfile.has(c.id) && (
                            <span
                              className="text-[10px] text-amber-600"
                              title="אין נתוני סביבת IT — ניתוח AI מבוסס-סביבה (כלל 31) לא יכול לעזור לקריאות של חברה זו עדיין"
                            >
                              ⚠
                            </span>
                          )}
                          <Link
                            to={`/companies/${c.id}/profile`}
                            className="text-xs text-indigo-600 hover:underline"
                          >
                            {profilesFailed || companyIdsWithProfile.has(c.id) ? "סביבה" : "הוסף סביבה"}
                          </Link>
                          <button
                            onClick={() => setEditing({ id: c.id, name: c.name, domain: c.domain, ai_instructions: c.ai_instructions || "" })}
                            className="text-xs text-brand-600 hover:underline"
                          >
                            עריכה
                          </button>
                          <button
                            onClick={() => { if (window.confirm(`למחוק את ${c.name}?`)) deleteMutation.mutate(c.id); }}
                            disabled={deleteMutation.isPending && deleteMutation.variables === c.id}
                            className="text-xs text-red-500 hover:underline disabled:opacity-50"
                          >
                            מחיקה
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
